"use client"
import { useState, useEffect } from "react"
import { ChevronRight, FileText, Loader } from "lucide-react"
import { postAws } from "../postAws"
import GiveMockTest from "./giveMockTest"

const examTypes = [
  { value: "board", label: "Board Exam" },
  { value: "jee", label: "JEE" },
  { value: "neet", label: "NEET" },
  { value: "upsc", label: "UPSC" },
  { value: "ssc", label: "SSC" },
]

const classes = {
  board: ["Class 11"],
  jee: ["Class 11"],
  neet: ["Class 11", "Class 12"],
  upsc: ["Prelims", "Mains"],
  ssc: ["CGL", "CHSL"],
}

const subjects = {
  board: {
    "Class 11": ["Mathematics", "Physics", "Chemistry"],
  },
  jee: {
    "Class 11": ["Mathematics", "Physics", "Chemistry"],
  },
}

const chapters = {
Mathematics: [
  "APPLICATION OF DERIVATIVES",
  "BINOMIAL THEOREM",
  "COMPLEX NUMBERS AND QUADRATIC EQUATIONS",
  "CONIC SECTION",
  "CONTINUITY AND DIFFERENTIABILITY",
  "PERMUTATIONS AND COMBINATIONS",
  "PROBABILITY",
  "SEQUENCES AND SERIES",
  "SETS",
  "TRIGONOMETRIC FUNCTIONS"
],

  Physics: ["MECHANICS", "THERMODYNAMICS", "WAVES AND OSCILLATIONS", "ELECTROSTATICS"],
  Chemistry: ["ATOMIC STRUCTURE", "CHEMICAL BONDING", "ORGANIC CHEMISTRY BASICS", "PERIODIC TABLE"],
}

export default function MockTestCreator() {
  const [selectedExamType, setSelectedExamType] = useState("")
  const [selectedClass, setSelectedClass] = useState("")
  const [selectedSubjects, setSelectedSubjects] = useState([])
  const [selectedChapters, setSelectedChapters] = useState([])
  const [currentStep, setCurrentStep] = useState("exam") // exam, class, subjects, chapters
  const [loading, setLoading] = useState(false)
  const [questions, setQuestions] = useState(null)
  const [error, setError] = useState(null)
  const [showMockTest, setShowMockTest] = useState(false)

  // Auto-select all chapters when subjects are selected
  useEffect(() => {
    if (selectedSubjects.length > 0) {
      const allChapters = []
      selectedSubjects.forEach((subject) => {
        if (chapters[subject]) {
          allChapters.push(...chapters[subject])
        }
      })
      setSelectedChapters(allChapters)
    }
  }, [selectedSubjects])

  const handleChapterChange = (chapter, checked) => {
    if (checked) {
      setSelectedChapters([...selectedChapters, chapter])
    } else {
      setSelectedChapters(selectedChapters.filter((c) => c !== chapter))
    }
  }

  const handleCreateTest = async () => {
    if (!selectedChapters.length) {
      alert("Please select at least one chapter")
      return
    }

    setLoading(true)
    setError(null)

    try {
      const payload = {
        request: "get-question-of-chapter-from-questionbank",
        body: {
          chapters: selectedChapters,
        },
      }

      console.log("Sending payload:", payload)
      const response = await postAws("https://api-dev.aurait.ai/QuestionBank", "POST", payload)
      console.log("API Response:", response)

      if (response && response.body && response.body.data) {
        setQuestions(response.body.data)
        setShowMockTest(true)
      } else {
        setError("Invalid response format")
      }
    } catch (err) {
      console.error("API Error:", err)
      setError("Failed to fetch questions. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const getAvailableChapters = () => {
    if (!selectedSubjects.length) return []
    const allChapters = []
    selectedSubjects.forEach((subject) => {
      if (chapters[subject]) {
        allChapters.push(...chapters[subject].map((chapter) => ({ chapter, subject })))
      }
    })
    return allChapters
  }

  const resetSelection = () => {
    setSelectedExamType("")
    setSelectedClass("")
    setSelectedSubjects([])
    setSelectedChapters([])
    setCurrentStep("exam")
  }

  // Show mock test component when questions are loaded
  if (showMockTest && questions) {
    return (
      <GiveMockTest
        questions={questions}
        onBack={() => {
          setShowMockTest(false)
          setQuestions(null)
        }}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Mock Test Creator</h1>
        </div>

        {/* Navigation Structure */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="p-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-6 text-sm text-gray-600">
              <span>Home</span>
              <ChevronRight className="h-4 w-4" />
              <span>Mock Test</span>
              {selectedExamType && (
                <>
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-gray-900 font-medium">
                    {examTypes.find((e) => e.value === selectedExamType)?.label}
                  </span>
                </>
              )}
              {selectedClass && (
                <>
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-gray-900 font-medium">{selectedClass}</span>
                </>
              )}
              {selectedSubjects.length > 0 && (
                <>
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-gray-900 font-medium">
                    {selectedSubjects.length === 1 ? selectedSubjects[0] : `${selectedSubjects.length} Subjects`}
                  </span>
                </>
              )}
            </div>

            {/* Step 1: Exam Type Selection */}
            {currentStep === "exam" && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Select Exam Type</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {examTypes.map((exam) => (
                    <button
                      key={exam.value}
                      onClick={() => {
                        setSelectedExamType(exam.value)
                        setCurrentStep("class")
                      }}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                    >
                      <span className="font-medium text-gray-900">{exam.label}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Class Selection */}
            {currentStep === "class" && selectedExamType && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Select Class</h3>
                  <button onClick={resetSelection} className="text-sm text-blue-600 hover:text-blue-800">
                    Reset Selection
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {classes[selectedExamType]?.map((cls) => (
                    <button
                      key={cls}
                      onClick={() => {
                        setSelectedClass(cls)
                        setCurrentStep("subjects")
                      }}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                    >
                      <span className="font-medium text-gray-900">{cls}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Subject Selection */}
            {currentStep === "subjects" && selectedClass && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Select Subjects</h3>
                  <button onClick={resetSelection} className="text-sm text-blue-600 hover:text-blue-800">
                    Reset Selection
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {subjects[selectedExamType]?.[selectedClass]?.map((subject) => (
                    <div
                      key={subject}
                      className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        id={subject}
                        checked={selectedSubjects.includes(subject)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSubjects([...selectedSubjects, subject])
                          } else {
                            setSelectedSubjects(selectedSubjects.filter((s) => s !== subject))
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={subject} className="text-sm cursor-pointer text-gray-700 flex-1 font-medium">
                        {subject}
                      </label>
                    </div>
                  ))}
                </div>

                {selectedSubjects.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Subjects:</h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedSubjects.map((subject) => (
                        <span
                          key={subject}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200"
                        >
                          {subject}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentStep("chapters")}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
                    >
                      Continue to Chapter Selection
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Chapter Selection */}
            {currentStep === "chapters" && selectedSubjects.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Select Chapters</h3>
                  <button onClick={resetSelection} className="text-sm text-blue-600 hover:text-blue-800">
                    Reset Selection
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {getAvailableChapters().map(({ chapter, subject }) => (
                    <div
                      key={`${subject}-${chapter}`}
                      className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        id={`${subject}-${chapter}`}
                        checked={selectedChapters.includes(chapter)}
                        onChange={(e) => handleChapterChange(chapter, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`${subject}-${chapter}`} className="text-sm cursor-pointer text-gray-700 flex-1">
                        <div className="font-medium">{chapter}</div>
                        <div className="text-xs text-gray-500">{subject}</div>
                      </label>
                    </div>
                  ))}
                </div>

                {selectedChapters.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Chapters:</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedChapters.map((chapter) => (
                        <span
                          key={chapter}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
                        >
                          {chapter}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Start Test Button */}
                {selectedChapters.length > 0 && (
                  <div className="pt-6 border-t border-gray-200">
                    <button
                      onClick={handleCreateTest}
                      disabled={loading}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader className="h-5 w-5 animate-spin" />
                          Loading Questions...
                        </>
                      ) : (
                        "Start Mock Test"
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Show instruction when on first step */}
            {currentStep === "exam" && (
              <div className="text-center py-8 mt-8 border-t border-gray-200">
                <div className="text-gray-400 mb-4">
                  <FileText className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Create Your Mock Test</h3>
                <p className="text-gray-600">
                  Follow the steps above to configure your test with multiple subjects and chapters
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>
    </div>
  )
}
